import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ListRange } from '@angular/cdk/collections';

import { NavigationMenuItem, SelectionItem } from '@shared/models';
import { MatSelectChange } from '@angular/material/select';
import { ActivateComponentService } from '@shared/services';
import { delay, filter, take, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  canDeactivateUnsavedChanges,
  ignoreNextGuardCall$,
} from '@shared/guards';

@Component({
  selector: 'app-navigation-select',
  templateUrl: './navigation-select.component.html',
  styleUrls: ['./navigation-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationSelectComponent implements OnChanges {
  @Input({ required: true }) items: NavigationMenuItem[];
  @Input() initialSelection: string | number;
  @Input() useDeactivationGuard = false;

  @Output() selectedItemChanges = new EventEmitter<string | number>();

  processedItems: SelectionItem[];
  navigationSelectFormControl = new FormControl<string | number>(null);
  // TODO: check if we can remove this once searchable-select-component has been refactored
  virtualizationRenderedRange: ListRange = { start: 0, end: 60 };

  private previousSelectedValue: number | string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private activatedComponentService: ActivateComponentService,
    private dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items) {
      this.processItems();
      this.initializeSelection();
    }
  }

  onSelectionChange(change: MatSelectChange): void {
    const selectedItem = this.processedItems.find((x) => x.id === change.value);

    if (!selectedItem) {
      return;
    }

    const deactivationGuardedComponent =
      this.activatedComponentService.activatedComponent.value;

    // if there is no deactivation guarded component, we can navigate directly
    if (!this.useDeactivationGuard || deactivationGuardedComponent == null) {
      this.navigateToSelectedItem(selectedItem);
      return;
    }

    canDeactivateUnsavedChanges(deactivationGuardedComponent, this.dialog)
      .pipe(
        take(1),
        tap((canDeactivate) => {
          // if the component cannot be deactivated, revert the current selected navigation value
          if (!canDeactivate) {
            this.navigationSelectFormControl.setValue(
              this.previousSelectedValue
            );
          }
        }),
        filter((canDeactivate) => canDeactivate),
        delay(0),
        tap(() => {
          ignoreNextGuardCall$.next(true);
          this.navigateToSelectedItem(selectedItem);
        })
      )
      .subscribe();
  }

  private processItems(): void {
    this.processedItems = this.items.map((x) => ({
      id: x.id,
      name: x.label,
      icon: x.icon,
      link: x.link,
    }));
  }

  private initializeSelection(): void {
    const selectedItem = this.initialSelection
      ? this.processedItems.find((x) => x.link === this.initialSelection)
      : this.processedItems[0];

    this.navigationSelectFormControl.setValue(
      selectedItem?.id || this.processedItems[0]?.id
    );

    this.previousSelectedValue = this.navigationSelectFormControl.value;
  }

  private navigateToSelectedItem(selectedItem: SelectionItem): void {
    this.selectedItemChanges.emit(selectedItem.link);

    this.router.navigate([selectedItem.link], {
      relativeTo: this.activatedRoute,
    });

    this.previousSelectedValue = this.navigationSelectFormControl.value;
  }
}
